(() => {
  if (customElements.get("restock-alert")) return;

class MissionGearRestockAlert extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = document.getElementById(this.dataset.formId);
    this.email = this.querySelector('input[name="contact[email]"]');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('[role="status"]');
    this.defaultButtonText = this.button?.textContent || "";
    this.alertSection = this.closest(".shopify-section") || this.parentElement;
    this.originalSection = this.alertSection;
    this.variants = this.readVariants();
    this.variantIds = new Set(this.variants.map((variant) => String(variant.id)));
    this.productSection = this.resolveVariantInput()?.closest(".shopify-section");
    this.scheduled = false;
    this.onChange = () => this.scheduleSync();
    this.onVariantChange = (event) => this.syncFromEvent(event);
    this.onSubmit = (event) => this.submit(event);
    this.pending = false;
    if (this.button) this.button.disabled = !this.form;

    document.addEventListener("change", this.onChange);
    document.addEventListener("input", this.onChange);
    document.addEventListener("variant:change", this.onVariantChange);
    document.addEventListener("product:variant-change", this.onVariantChange);
    this.form?.addEventListener("submit", this.onSubmit);

    this.observer = new MutationObserver(this.onChange);
    const observedSection = this.productSection || this.alertSection;
    if (observedSection) {
      this.observer.observe(observedSection, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["value", "checked", "selected"],
      });
    }
    this.placeInline();
    this.sync();
  }

  disconnectedCallback() {
    queueMicrotask(() => {
    if (this.isConnected) return;
    document.removeEventListener("change", this.onChange);
    document.removeEventListener("input", this.onChange);
    document.removeEventListener("variant:change", this.onVariantChange);
    document.removeEventListener("product:variant-change", this.onVariantChange);
    this.form?.removeEventListener("submit", this.onSubmit);
    this.observer?.disconnect();
    this.initialized = false;
    });
  }

  readVariants() {
    try {
      return JSON.parse(
        this.querySelector("[data-restock-variants]")?.textContent || "[]",
      );
    } catch {
      return [];
    }
  }

  scheduleSync() {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      this.sync();
    });
  }

  syncFromEvent(event) {
    const variant = event.detail?.variant || event.detail;
    const productId = String(variant?.product_id || variant?.productId || "");
    if (productId && productId !== this.dataset.productId) return;
    if (variant?.id) this.updateVariant(String(variant.id));
    else this.scheduleSync();
  }

  sync() {
    this.placeInline();
    const variantInput = this.resolveVariantInput();
    const urlVariant = new URL(window.location.href).searchParams.get("variant");
    const variantId = String(variantInput?.value || urlVariant || this.dataset.selectedVariant || "");
    this.updateVariant(variantId);
  }

  placeInline() {
    if (window.Shopify?.designMode) return;

    const variantInput = this.resolveVariantInput();
    const productSection = variantInput?.closest(".shopify-section");
    const quantityItem = productSection?.querySelector(
      ".product-form__info-item--quantity",
    );
    const infoList = quantityItem?.closest(".product-form__info-list");

    if (!quantityItem || !infoList) return;
    if (this.parentElement !== infoList || this.nextElementSibling !== quantityItem) {
      infoList.insertBefore(this, quantityItem);
    }

    this.dataset.inline = "true";
    if (this.originalSection && this.originalSection !== productSection && !this.originalSection.contains(this)) {
      this.originalSection.hidden = true;
    }
  }

  resolveVariantInput() {
    return Array.from(
      document.querySelectorAll('form[action*="/cart/add"] [name="id"]'),
    ).find((input) => this.variantIds?.has(String(input.value)));
  }

  updateVariant(variantId) {
    const variant = this.variants.find((item) => String(item.id) === variantId);
    if (!variant) return;
    const changed = this.dataset.selectedVariant !== variantId;
    this.dataset.selectedVariant = variantId;
    this.hidden = Boolean(variant.available);
    if (changed) this.resetStatus();
  }

  resetStatus() {
    if (this.status) {
      this.status.textContent = "";
      delete this.status.dataset.error;
    }
    if (this.button) {
      this.button.disabled = this.pending;
      this.button.textContent = this.pending ? this.dataset.sending : this.defaultButtonText;
    }
  }

  async submit(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.pending || !this.form?.reportValidity()) return;
    this.sync();
    if (this.hidden) return;
    const selectedVariant = this.dataset.selectedVariant;
    const endpoint = new URL(this.dataset.endpoint, window.location.origin);
    if (endpoint.protocol !== 'https:') return;
    this.pending = true;
    this.button.disabled = true;
    this.button.textContent = this.dataset.sending;
    this.status.textContent = '';
    delete this.status.dataset.error;
    this.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(endpoint.href, {
        method: 'POST', credentials: 'omit', signal: controller.signal,
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          email: this.email.value,
          variantId: selectedVariant,
          productHandle: this.dataset.productHandle,
          locale: this.dataset.language.toLowerCase(),
          consent: this.querySelector('[name="restock_consent"]').checked,
          company: this.querySelector('[name="company"]').value,
        }),
      });
      const result = await response.json();
      if (this.dataset.selectedVariant !== selectedVariant) return;
      if (!response.ok || !result.ok) {
        this.status.textContent = result.code === 'RATE_LIMITED' ? this.dataset.rateLimited : this.dataset.error;
        this.status.dataset.error = 'true';
      } else {
        this.status.textContent = result.code === 'ALREADY_AVAILABLE' ? this.dataset.available : this.dataset.success;
      }
    } catch {
      if (this.dataset.selectedVariant === selectedVariant) {
        this.status.textContent = this.dataset.error;
        this.status.dataset.error = 'true';
      }
    } finally {
      clearTimeout(timeout);
      this.pending = false;
      this.removeAttribute('aria-busy');
      this.button.disabled = false;
      this.button.textContent = this.defaultButtonText;
    }
  }
}

if (!customElements.get("restock-alert")) {
  customElements.define("restock-alert", MissionGearRestockAlert);
}
})();
